import api from './api';

export interface AvailabilitySettings {
  minimumLeadTime: string; // Format: "20:00:00" (HH:mm:ss)
  maximumAdvance: string;  // Format: "10.00:00:00" (DD.HH:mm:ss)
}

export interface AvailabilityResponse {
  minimumLeadTime: string;
  maximumAdvance: string;
}

export interface DateRange {
  minDate: Date;
  maxDate: Date;
  minDateTime: Date;
  maxDateTime: Date;
}

export class AvailabilityService {
  
  /**
   * Fetch availability settings from API with optional serviceId
   */
  static async getAvailability(serviceId?: string): Promise<AvailabilitySettings> {
    try {
      
      const params = serviceId ? { serviceId } : {};
      const response = await api.get('/api/Application/v1/GetAvailability', { params });
      
      
      return {
        minimumLeadTime: response.data.minimumLeadTime || "00:00:00",
        maximumAdvance: response.data.maximumAdvance || "365.00:00:00"
      };
    } catch (error) {
      console.error('❌ Error fetching availability settings:', error);
      
      // Return default values if API fails
      return {
        minimumLeadTime: "00:00:00",
        maximumAdvance: "365.00:00:00"
      };
    }
  }

  private static parseDurationToHours(durationString: string, fallbackHours: number): number {
    try {
      if (!durationString) {
        return fallbackHours;
      }

      const normalized = durationString.trim();

      if (normalized.startsWith('P')) {
        const isoMatch = normalized.match(/^P(?:(\d+(?:\.\d+)?)W)?(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i);
        if (!isoMatch) {
          return fallbackHours;
        }

        const weeks = Number(isoMatch[1] || 0);
        const days = Number(isoMatch[2] || 0);
        const hours = Number(isoMatch[3] || 0);
        const minutes = Number(isoMatch[4] || 0);
        const seconds = Number(isoMatch[5] || 0);

        return (weeks * 7 * 24) + (days * 24) + hours + (minutes / 60) + (seconds / 3600);
      }

      const dotParts = normalized.split('.');
      if (dotParts.length === 2) {
        const days = Number(dotParts[0]);
        const [hours, minutes, seconds] = dotParts[1].split(':').map(Number);
        const totalHours = (days * 24) + hours + (minutes / 60) + (seconds / 3600);
        return Number.isFinite(totalHours) ? totalHours : fallbackHours;
      }

      const [hours, minutes, seconds] = normalized.split(':').map(Number);
      const totalHours = hours + (minutes / 60) + (seconds / 3600);
      return Number.isFinite(totalHours) ? totalHours : fallbackHours;
    } catch (error) {
      console.error('❌ Error parsing duration string:', durationString, error);
      return fallbackHours;
    }
  }

  /**
   * Parse lead time strings in Graph ISO-8601 or .NET TimeSpan format to hours.
   */
  static parseTimeToHours(timeString: string): number {
    return this.parseDurationToHours(timeString, 0);
  }

  /**
   * Parse advance time strings in Graph ISO-8601 or .NET TimeSpan format to hours.
   */
  static parseAdvanceToHours(advanceString: string): number {
    return this.parseDurationToHours(advanceString, 365 * 24);
  }

  /**
   * Calculate the available date range based on availability settings
   */
  static calculateDateRange(settings: AvailabilitySettings): DateRange {
    const now = new Date();
    

    // Calculate minimum lead time
    const leadTimeHours = this.parseTimeToHours(settings.minimumLeadTime);
    const minDateTime = new Date(now.getTime() + (leadTimeHours * 60 * 60 * 1000));
    
    // Calculate maximum advance time  
    const advanceHours = this.parseAdvanceToHours(settings.maximumAdvance);
    const maxDateTime = new Date(now.getTime() + (advanceHours * 60 * 60 * 1000));

    // Extract dates (set to start of day for date picker)
    const minDate = new Date(minDateTime.getFullYear(), minDateTime.getMonth(), minDateTime.getDate());
    const maxDate = new Date(maxDateTime.getFullYear(), maxDateTime.getMonth(), maxDateTime.getDate());

    const result = {
      minDate,
      maxDate,
      minDateTime,
      maxDateTime
    };

    return result;
  }

  /**
   * Check if a date should be disabled based on availability settings
   */
  static shouldDisableDate(date: Date, dateRange: DateRange): boolean {
    const dateToCheck = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const isBeforeMin = dateToCheck < dateRange.minDate;
    const isAfterMax = dateToCheck > dateRange.maxDate;
    
    const shouldDisable = isBeforeMin || isAfterMax;
    
    
    return shouldDisable;
  }

  /**
   * Filter time slots based on availability settings
   */
  static filterTimeSlots<T extends { startTime: string; endTime: string }>(
  timeSlots: T[],
  selectedDate: Date,
  dateRange: DateRange
): T[] {
  if (!timeSlots || timeSlots.length === 0) {
    return [];
  }


  const filtered = timeSlots.filter(slot => {
    try {

      const slotStartDate = new Date(slot.startTime);
      const slotEndDate = new Date(slot.endTime);

      // Get the date components (these are in EST)
      const startYear = slotStartDate.getFullYear();
      const startMonth = slotStartDate.getMonth();
      const startDay = slotStartDate.getDate();
      const startHour = slotStartDate.getHours();
      const startMinute = slotStartDate.getMinutes();

      const endYear = slotEndDate.getFullYear();
      const endMonth = slotEndDate.getMonth();
      const endDay = slotEndDate.getDate();
      const endHour = slotEndDate.getHours();
      const endMinute = slotEndDate.getMinutes();

      // Create new dates representing these times in EST
      // For May 2025, EST is actually EDT (UTC-4)
      const estOffsetHours = 4; // EDT offset from UTC

      // Create UTC times by adding the EST offset
      const slotStartUTC = new Date(Date.UTC(startYear, startMonth, startDay, startHour + estOffsetHours, startMinute));
      const slotEndUTC = new Date(Date.UTC(endYear, endMonth, endDay, endHour + estOffsetHours, endMinute));

      // Now compare these UTC times with the availability window
      const isStartValid = slotStartUTC >= dateRange.minDateTime;
      const isEndValid = slotEndUTC <= dateRange.maxDateTime;
      const isValid = isStartValid && isEndValid;

      return isValid;
    } catch (error) {
      console.error('❌ Error filtering time slot:', slot, error);
      return false;
    }
  });


  return filtered;
}

  /**
   * Check if time slot should be disabled (for display purposes)
   */
  static shouldDisableTimeSlot(
    slot: { startTime: string; endTime: string }, 
    selectedDate: Date, 
    dateRange: DateRange
  ): boolean {
    try {
      const slotStart = new Date(slot.startTime);
      const slotEnd = new Date(slot.endTime);

      const isStartTooEarly = slotStart < dateRange.minDateTime;
      const isEndTooLate = slotEnd > dateRange.maxDateTime;
      
      return isStartTooEarly || isEndTooLate;
    } catch (error) {
      console.error('❌ Error checking time slot availability:', error);
      return true; // Disable on error
    }
  }
}
